import {
  InfiniteData,
  isCancelledError,
  Query,
  QueryClient,
  QueryKey,
} from '@tanstack/react-query'
import type {AxiosResponse} from 'axios'

import {EndpointItem, EndpointsSearchResponse} from 'interfaces'
import {GET_ENDPOINTS_QUERY_KEY} from 'features/Endpoints/hooks/useEndpointsQuery'

type EndpointsSearchPages = InfiniteData<AxiosResponse<EndpointsSearchResponse>>

type EndpointIds = {
  agentUUID: string
  userUUID: string
}

type ToggleSent = EndpointIds & {
  disable: boolean
}

export const withToggleSent = (
  endpoint: EndpointItem,
  disable: boolean,
  sentAt: string,
): EndpointItem => {
  // "Pending" needs the request to be newer than the endpoint's last config
  // fetch. A browser clock running behind the server must not hide it, so
  // never stamp it earlier than that fetch.
  const configTime = new Date(endpoint.configurationLastUpdated).getTime()
  const sentTime = new Date(sentAt).getTime()
  const timestamp = Number.isNaN(configTime)
    ? sentAt
    : new Date(Math.max(sentTime, configTime + 1)).toISOString()
  return {...endpoint, adminSetState: {enabled: !disable, timestamp}}
}

const isEndpoint =
  ({agentUUID, userUUID}: EndpointIds) =>
  (endpoint: EndpointItem): boolean =>
    endpoint.agentUUID === agentUUID && endpoint.userUUID === userUUID

const hasEndpoint = (
  cached: EndpointsSearchPages | undefined,
  ids: EndpointIds,
): boolean =>
  Boolean(
    cached?.pages?.some(page =>
      page?.data?.data?.endpoints?.some(isEndpoint(ids)),
    ),
  )

const updateEndpointInPages = (
  cached: EndpointsSearchPages | undefined,
  ids: EndpointIds,
  update: (endpoint: EndpointItem) => EndpointItem,
): EndpointsSearchPages | undefined => {
  // Leave lists without the endpoint untouched, so their rows aren't rebuilt.
  if (!cached?.pages || !hasEndpoint(cached, ids)) return cached
  const matches = isEndpoint(ids)
  return {
    ...cached,
    pages: cached.pages.map(page => {
      const endpoints = page?.data?.data?.endpoints
      if (!endpoints) return page
      return {
        ...page,
        data: {
          ...page.data,
          data: {
            ...page.data.data,
            endpoints: endpoints.map(endpoint =>
              matches(endpoint) ? update(endpoint) : endpoint,
            ),
          },
        },
      }
    }),
  }
}

const patchPages = (
  cached: EndpointsSearchPages | undefined,
  toggle: ToggleSent,
  sentAt: string,
): EndpointsSearchPages | undefined =>
  updateEndpointInPages(cached, toggle, endpoint =>
    withToggleSent(endpoint, toggle.disable, sentAt),
  )

type EndpointsQuery = Query<
  EndpointsSearchPages,
  unknown,
  EndpointsSearchPages,
  QueryKey
>

// An infinite query loading its next page (not refetching), including one
// paused while offline.
const isLoadingNextPage = (query: EndpointsQuery): boolean =>
  query.state.fetchStatus !== 'idle' &&
  query.state.fetchMeta?.fetchMore !== undefined

// The page being loaded is built on the rows from before the patch, and a
// cancelled load restores them too, so mark the endpoint again after either.
// A load that fails keeps the patched rows; writing then would clear its error.
const patchWhenNextPageLands = (
  queryClient: QueryClient,
  query: EndpointsQuery,
  toggle: ToggleSent,
  sentAt: string,
): void => {
  const unsubscribe = queryClient.getQueryCache().subscribe(event => {
    if (event.query !== query) return
    if (event.type === 'removed') {
      unsubscribe()
      return
    }
    if (event.type !== 'updated' || query.state.fetchStatus !== 'idle') return
    unsubscribe()
    const {action} = event
    const isCancelled =
      action.type === 'error' && isCancelledError(action.error)
    if (action.type !== 'success' && !isCancelled) return
    queryClient.setQueryData<EndpointsSearchPages>(query.queryKey, cached =>
      patchPages(cached, toggle, sentAt),
    )
  })
}

/**
 * Records an enable or disable the server accepted on the cached endpoint
 * lists, so the list, its later pages and the panel show it pending at once.
 * The list derives "pending" from adminSetState, so only that changes here.
 */
export const markToggleSentInCache = async (
  queryClient: QueryClient,
  toggle: ToggleSent,
  sentAt: string,
): Promise<void> => {
  const queries = queryClient
    .getQueryCache()
    .findAll({queryKey: [GET_ENDPOINTS_QUERY_KEY]}) as EndpointsQuery[]

  // Never cancel a next-page load: the scroll list only asks for a page once,
  // so a dropped page would stop it loading more. Mark it after it lands.
  const loadingNextPage = queries.filter(isLoadingNextPage)
  loadingNextPage.forEach(query =>
    patchWhenNextPageLands(queryClient, query, toggle, sentAt),
  )

  // Other lists showing the endpoint: a refetch would land after this patch
  // with the old state, so cancel it first (it restores the previous data).
  const showingEndpoint = queries.filter(
    query =>
      !loadingNextPage.includes(query) && hasEndpoint(query.state.data, toggle),
  )
  await Promise.all(
    showingEndpoint.map(query =>
      queryClient.cancelQueries({queryKey: query.queryKey, exact: true}),
    ),
  )
  const toPatch = [...loadingNextPage, ...showingEndpoint]
  toPatch.forEach(query =>
    queryClient.setQueryData<EndpointsSearchPages>(query.queryKey, cached =>
      patchPages(cached, toggle, sentAt),
    ),
  )
}

/**
 * Records a Debug mode change the server accepted on the cached endpoint
 * lists. The list rebuilds its rows from this cache (for example when a toggle
 * is marked pending), and the open endpoint follows its row, so without this
 * a rebuild would bring back the old Debug state.
 */
export const markDebugSentInCache = (
  queryClient: QueryClient,
  ids: EndpointIds,
  debugMode: boolean,
): void => {
  queryClient.setQueriesData<EndpointsSearchPages>(
    {queryKey: [GET_ENDPOINTS_QUERY_KEY]},
    cached =>
      updateEndpointInPages(cached, ids, endpoint => {
        if (!debugMode) return {...endpoint, debugState: '0'}
        // Keep a known Debug level; the server sets the real one later.
        const isOn = Number(endpoint.debugState) > 0
        return {...endpoint, debugState: isOn ? endpoint.debugState : '1'}
      }),
  )
}
